/**
 * PhysicsEngine - Handles gravity, velocity, and platformer physics
 * Manages physics simulation for all entities in the game
 */

import type { Vector2 } from '../types/game';
import type { Entity, Physics } from '../types/entities';

export class PhysicsEngine {
  // Physics constants
  private gravity: Vector2 = { x: 0, y: -1500 }; // pixels/second^2 (downward) — matches jumpForce for symmetric arc
  private terminalVelocity: number = 1500; // max fall speed — matches jumpForce
  private defaultFriction: number = 0.8; // ground friction
  private airResistance: number = 0.98; // air friction

  // Physics components for entities
  private physicsComponents: Map<string, Physics> = new Map();
  private entities: Map<string, Entity> = new Map();

  // Performance tracking
  private enabled: boolean = true;

  constructor() {
    console.log('PhysicsEngine: Initialized');
  }

  /**
   * Register an entity with physics
   */
  public addEntity(entity: Entity, physicsConfig?: Partial<Physics>): void {
    this.entities.set(entity.id, entity);

    // Create default physics component
    const physics: Physics = {
      entityId: entity.id,
      mass: physicsConfig?.mass ?? 1.0,
      gravity: physicsConfig?.gravity ?? 1.0, // gravity multiplier
      friction: physicsConfig?.friction ?? this.defaultFriction,
      restitution: physicsConfig?.restitution ?? 0.0, // no bounce by default
      isKinematic: physicsConfig?.isKinematic ?? false,
      isGrounded: physicsConfig?.isGrounded ?? false,
      canJump: physicsConfig?.canJump ?? false,
    };

    this.physicsComponents.set(entity.id, physics);
  }

  /**
   * Remove an entity from physics simulation
   */
  public removeEntity(entityId: string): void {
    this.entities.delete(entityId);
    this.physicsComponents.delete(entityId);
  }

  /**
   * Get physics component for an entity
   */
  public getPhysics(entityId: string): Physics | undefined {
    return this.physicsComponents.get(entityId);
  }

  /**
   * Update physics for all entities
   */
  public update(deltaTime: number): void {
    if (!this.enabled) {
      return;
    }

    // Cap delta time to prevent physics explosion
    const dt = Math.min(deltaTime, 0.033); // Max 33ms (30 FPS minimum)

    for (const [entityId, entity] of this.entities) {
      const physics = this.physicsComponents.get(entityId);
      if (!physics) {
        continue;
      }

      if (physics.isKinematic) {
        // Kinematic objects: only apply velocity (no gravity, no friction)
        // This allows controlled movement via moveHorizontal()
        this.applyVelocity(entity, dt);
        continue;
      }

      // Apply gravity
      this.applyGravity(entity, physics, dt);

      // Apply friction/resistance
      this.applyFriction(entity, physics, dt);

      // Update position based on velocity
      this.applyVelocity(entity, dt);

      // Clamp velocity to prevent extreme speeds
      this.clampVelocity(entity);
    }
  }

  /**
   * Apply gravity to an entity
   */
  private applyGravity(entity: Entity, physics: Physics, deltaTime: number): void {
    // Skip if gravity is disabled (gravity multiplier = 0)
    if (physics.gravity === 0) {
      return;
    }

    // Don't apply gravity if grounded and not moving upward
    if (physics.isGrounded && entity.velocity.y <= 0) {
      entity.velocity.y = 0;
      return;
    }

    // Apply gravity force: v = v + g * t * gravityMultiplier
    entity.velocity.y += this.gravity.y * physics.gravity * deltaTime;

    // Clamp to terminal velocity
    if (entity.velocity.y < -this.terminalVelocity) {
      entity.velocity.y = -this.terminalVelocity;
    }
  }

  /**
   * Apply friction and air resistance
   */
  private applyFriction(entity: Entity, physics: Physics, deltaTime: number): void {
    if (physics.isGrounded) {
      // Ground friction - stronger
      entity.velocity.x *= physics.friction;

      // Stop if velocity is very small (prevents sliding forever)
      if (Math.abs(entity.velocity.x) < 1) {
        entity.velocity.x = 0;
      }
    } else {
      // Air resistance - only horizontal, not vertical (keeps jump arc symmetric)
      entity.velocity.x *= this.airResistance;
    }
  }

  /**
   * Update entity position based on velocity
   */
  private applyVelocity(entity: Entity, deltaTime: number): void {
    entity.position.x += entity.velocity.x * deltaTime;
    entity.position.y += entity.velocity.y * deltaTime;
  }

  /**
   * Clamp velocity to prevent extreme speeds
   */
  private clampVelocity(entity: Entity): void {
    const maxHorizontalSpeed = 1000; // pixels/second
    const maxVerticalSpeed = this.terminalVelocity;

    // Clamp horizontal velocity
    if (entity.velocity.x > maxHorizontalSpeed) {
      entity.velocity.x = maxHorizontalSpeed;
    } else if (entity.velocity.x < -maxHorizontalSpeed) {
      entity.velocity.x = -maxHorizontalSpeed;
    }

    // Clamp vertical velocity - only cap downward (falling), allow any upward (jumping) speed
    // Terminal velocity should only limit fall speed, not jump height
    if (entity.velocity.y < -this.terminalVelocity) {
      entity.velocity.y = -this.terminalVelocity;
    }
  }

  /**
   * Apply an impulse (instant force) to an entity
   */
  public applyImpulse(entityId: string, impulse: Vector2): void {
    const entity = this.entities.get(entityId);
    if (!entity) {
      return;
    }

    entity.velocity.x += impulse.x;
    entity.velocity.y += impulse.y;
  }

  /**
   * Apply a force over time to an entity
   */
  public applyForce(entityId: string, force: Vector2, deltaTime: number): void {
    const entity = this.entities.get(entityId);
    const physics = this.physicsComponents.get(entityId);
    if (!entity || !physics) {
      return;
    }

    // F = ma, so a = F/m
    const acceleration = {
      x: force.x / physics.mass,
      y: force.y / physics.mass,
    };

    entity.velocity.x += acceleration.x * deltaTime;
    entity.velocity.y += acceleration.y * deltaTime;
  }

  /**
   * Set velocity for an entity
   */
  public setVelocity(entityId: string, velocity: Vector2): void {
    const entity = this.entities.get(entityId);
    if (!entity) {
      return;
    }

    entity.velocity.x = velocity.x;
    entity.velocity.y = velocity.y;
  }

  /**
   * Set grounded state for an entity
   */
  public setGrounded(entityId: string, grounded: boolean): void {
    const physics = this.physicsComponents.get(entityId);
    if (!physics) {
      return;
    }

    physics.isGrounded = grounded;
    physics.canJump = grounded; // Can jump when grounded
  }

  /**
   * Set gravity multiplier for an entity (0 = no gravity, 1 = normal)
   */
  public setEntityGravity(entityId: string, multiplier: number): void {
    const physics = this.physicsComponents.get(entityId);
    if (physics) physics.gravity = multiplier;
  }

  /**
   * Set canJump independently (used for coyote time)
   */
  public setCanJump(entityId: string, canJump: boolean): void {
    const physics = this.physicsComponents.get(entityId);
    if (physics) physics.canJump = canJump;
  }

  /**
   * Make entity jump (if grounded)
   */
  public jump(entityId: string, jumpForce: number): boolean {
    const entity = this.entities.get(entityId);
    const physics = this.physicsComponents.get(entityId);

    if (!entity || !physics || !physics.canJump) {
      return false;
    }

    // Apply upward impulse
    entity.velocity.y = jumpForce;
    physics.isGrounded = false;
    physics.canJump = false;

    return true;
  }

  /**
   * Move entity horizontally (platformer movement)
   */
  public moveHorizontal(entityId: string, direction: number, speed: number): void {
    const entity = this.entities.get(entityId);
    const physics = this.physicsComponents.get(entityId);

    if (!entity || !physics) {
      return;
    }

    // Set horizontal velocity based on direction and speed
    // direction: -1 (left), 0 (stop), 1 (right)
    entity.velocity.x = direction * speed;
  }

  /**
   * Stop entity movement
   */
  public stop(entityId: string): void {
    const entity = this.entities.get(entityId);
    if (!entity) {
      return;
    }

    entity.velocity.x = 0;
    entity.velocity.y = 0;
  }

  /**
   * Set global gravity
   */
  public setGravity(gravity: Vector2): void {
    this.gravity = gravity;
  }

  /**
   * Get global gravity
   */
  public getGravity(): Vector2 {
    return { ...this.gravity };
  }

  /**
   * Enable/disable physics engine
   */
  public setEnabled(enabled: boolean): void {
    this.enabled = enabled;
  }

  /**
   * Check if entity is grounded
   */
  public isGrounded(entityId: string): boolean {
    const physics = this.physicsComponents.get(entityId);
    return physics?.isGrounded ?? false;
  }

  /**
   * Check if entity can jump
   */
  public canJump(entityId: string): boolean {
    const physics = this.physicsComponents.get(entityId);
    return physics?.canJump ?? false;
  }

  /**
   * Get entity count
   */
  public getEntityCount(): number {
    return this.entities.size;
  }

  /**
   * Clear all entities
   */
  public clear(): void {
    this.entities.clear();
    this.physicsComponents.clear();
  }

  /**
   * Get performance stats
   */
  public getStats(): PhysicsStats {
    return {
      entityCount: this.entities.size,
      enabled: this.enabled,
      gravity: { ...this.gravity },
    };
  }
}

export interface PhysicsStats {
  entityCount: number;
  enabled: boolean;
  gravity: Vector2;
}

// Singleton instance
let physicsEngineInstance: PhysicsEngine | null = null;

/**
 * Get the singleton PhysicsEngine instance
 */
export function getPhysicsEngine(): PhysicsEngine {
  if (!physicsEngineInstance) {
    physicsEngineInstance = new PhysicsEngine();
  }
  return physicsEngineInstance;
}
